import React from "react";
import { TextFieldVariant, Typeahead } from "@merge-api/merge-javascript-shared";
import {
  ConditionPreset,
  ConditionPresetMeta,
  ConditionType,
  APICategory,
} from "../../../../../../models/Entities";
import { getEnumValueOptions, getOperatorValue } from "../helpers";
import EnumValueSelect from "./EnumValueSelect";
import UpsellFilterOptionMessage from "./UpsellFilterOptionMessage";
import ValueTextField from "./ValueTextField";

type GenericValueFieldProps = {
  conditionPreset: Partial<ConditionPreset>;
  conditionPresetMeta: ConditionPresetMeta;
  onConditionPresetChange: (ConditionPreset: Partial<ConditionPreset>) => void;
  showGatedSelectiveSync: boolean;
  disabled?: boolean;
  category: APICategory;
};

const BOOLEAN_OPTIONS: boolean[] = [true, false];

const GenericValueField = ({
  conditionPreset,
  conditionPresetMeta,
  onConditionPresetChange,
  showGatedSelectiveSync,
  disabled,
  category,
}: GenericValueFieldProps) => {
  const operatorValue = getOperatorValue(conditionPreset, conditionPresetMeta, category);
  const enumValueOptions = getEnumValueOptions(conditionPreset, conditionPresetMeta);

  if (operatorValue?.key_type === ConditionType.BOOLEAN) {
    // value can be a real boolean stored in JSON; coerce string forms so older saved presets still match an option
    const rawValue = conditionPreset.value as boolean | string | null | undefined;
    const value: boolean | undefined =
      typeof rawValue === "boolean"
        ? rawValue
        : rawValue === "true"
        ? true
        : rawValue === "false"
        ? false
        : undefined;

    return (
      <div key="boolean-value" className="flex flex-1">
        {showGatedSelectiveSync ? (
          <Typeahead
            className="h-full py-[3px]"
            borderVariant={TextFieldVariant.InlinedRounded}
            placeholder="Select value..."
            options={value !== undefined ? [value] : []}
            disableClearable
            getOptionLabel={(option) => (option ? "true" : "false")}
            value={value}
            typeaheadFooter={<UpsellFilterOptionMessage />}
            disabled={disabled}
          />
        ) : (
          <Typeahead
            className="h-full py-[3px]"
            borderVariant={TextFieldVariant.InlinedRounded}
            placeholder="Select value..."
            options={BOOLEAN_OPTIONS}
            disableClearable
            getOptionLabel={(option) => (option ? "true" : "false")}
            value={value}
            onChange={(_: any, valueOption: boolean | null) => {
              onConditionPresetChange({
                ...conditionPreset,
                // backend JSONField round-trips real booleans; cast here because the shared type is string|null
                value: (valueOption ?? undefined) as unknown as string,
                is_end_user_configured: false,
              });
            }}
            disabled={disabled}
          />
        )}
      </div>
    );
  }

  return enumValueOptions && enumValueOptions.length > 0 ? (
    <EnumValueSelect
      key="enum-value"
      conditionPreset={conditionPreset}
      onConditionPresetChange={onConditionPresetChange}
      showGatedSelectiveSync={showGatedSelectiveSync}
      disabled={disabled}
      enumValueOptions={enumValueOptions}
    />
  ) : (
    <ValueTextField
      key="value"
      conditionPreset={conditionPreset}
      conditionPresetMeta={conditionPresetMeta}
      onConditionPresetChange={onConditionPresetChange}
      category={category}
      disabled={disabled}
    />
  );
};

export default GenericValueField;
